import { ColumnGroupShowType, IAggFunc, IDateFilterParams, INumberFilterParams, ISetFilterParams, ITextFilterParams, ValueFormatterFunc, ValueGetterFunc } from "ag-grid-enterprise";
import { SecurityRuleKeys } from "../../../core/security";
import { HierarchyType } from "../../hierarchy";
import { ReportColumnGroupKeys, ReportColumnKeys } from "./report-column.registry";


export type ColumnQueryState = {
    filterOperator?: ColumnFilterOperator,
    filterValue?: string | string[],
    sortDirection?: SortDirection,
    sortIndex?: number
}
export type SortDirection = 'asc' | 'desc';
export type ColumnFilterOperator =
  | 'equals'
  | 'notEqual'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'blank'
  | 'notBlank';


/** Supported data types for report columns.
 * - Determines how values are formatted, sorted, and filtered in the grid.
 */
export type SupportedColumnType = 
    | 'boolean'
    | 'currency'
    | 'date'
    | 'dateMonth'
    | 'dateTime' 
    | 'dateYear'
    | 'number'
    | 'percentage'
    | 'text'
    | 'time';

export type SupportedColumnFilterType = 
    | 'text'
    | 'number'
    | 'date'
    | 'set';

export const COLUMN_TYPE_FILTER_MAP: Record<SupportedColumnType, SupportedColumnFilterType | null> = {
    boolean: 'set',
    currency: 'number',
    date: 'date',
    dateMonth: 'set',
    dateTime: 'date',
    dateYear: 'number',
    number: 'number',
    percentage: 'number',
    text: 'text',
    time: null,
}


/**
 * Aggregation function types supported by AG Grid and reporting system.
 * Can be a built-in function name, a custom aggregation function, or null
 * (for columns that should not aggregate by default).
 */
type aggFunc = 
    | null
    | 'sum'
    | 'avg'
    | 'count'
    | 'min'
    | 'max'
    | 'first'
    | 'last'
    | IAggFunc
    | string;



/**
 * Defines a single report column used within the reporting system.
 * 
 * This interface wraps AG Grid's `ColDef` with additional metadata
 * used for report configuration, security, and hierarchy awareness.
 * 
 * Each column in a report is defined in the Column Registry
 * and can be overridden within a report layout.
 */
export interface ReportColumnDefinition {

    /** Unique identifier for this column within the report registry. */
    key: string;


    /** Optional source property name in the data set.
     * - Defaults to `key` if not specified.*/
    dataField?: string;


    /** Underlying data key used for lookups or related entity actions. */
    dataKeyField?: string;


    /** Column header text displayed in the grid.
     * - NOTE: Required even though AG Grid defaults to the field name. */
    header: string;


    /* -------------------------------------------------------------------------- */
    /*                               Presentation                                 */
    /* -------------------------------------------------------------------------- */
    
    /** Data type (e.g., 'text', 'number', 'date', 'boolean') */
    type: SupportedColumnType;

    /** Whether the column is initially visible. @default false */
    visible?: boolean;

    /** Display order for columns.
     * - Columns without an order value appear last, sorted alphabetically. */
    order?: number;

    /** Default sort direction (e.g., 'asc' | 'desc'). */
    sortDirection?: SortDirection;

    /** Sort priority when multiple columns are sorted. */
    sortIndex?: number;

    /** Optional column group association key for grouping headers. */
    columnGroupKey?: ReportColumnGroupKeys;

    /** Defines when the column is shown within its group.
    * - Useful when some columns should only appear when the group is expanded. */
    columnGroupShow?: ColumnGroupShowType | undefined;

    /** Number of decimal places to show for numeric values.  */
    decimalPlaces?: number;

    /** Function or expression that extracts a value from the row data. */
    valueGetter?: ValueGetterFunc | null;

    /** Function or expression that formats a value for display. */
    valueFormatter?: ValueFormatterFunc | null;

    /** Pin a column to one side. `right` or `left`.  A value of `true` is pinned to the `left`. */
    pinned?: boolean | 'left' | 'right' | null;

    /** Column width in Pixels. When undefined or null, the grid's default column width is used. */
    width?: number | null;
    


    /* -------------------------------------------------------------------------- */
    /*                             Grouping / Pivoting                            */
    /* -------------------------------------------------------------------------- */
    
    /** Allows the column to be used as a row group in the GUI. @default false */
    enableRowGroup?: boolean;

    /** Whether this column is grouped by default. */
    rowGroup?: boolean;

    /** Allows the column to be used as a pivot dimension. 
     * - NOTE: Pivoting should only be enabled for low-cardinality categorical fields
     * (e.g., Month, Region, Product, Trade Class) to avoid excessive group combinations.
     * @default false */
    enablePivot?: boolean;

    /** Whether this column is pivoted by default. */
    pivot?: boolean;

   
    /* -------------------------------------------------------------------------- */
    /*                                  Filtering                                 */
    /* -------------------------------------------------------------------------- */
   
    /** Filter type for this column. */
    filterType?: SupportedColumnFilterType;

    /** Additional filter configuration options. */
    filterParams?: ITextFilterParams | INumberFilterParams | IDateFilterParams | ISetFilterParams;

    /** Value getter used specifically by the filter (if different from valueGetter). */
    filterValueGetter?: null | ValueGetterFunc


    /* -------------------------------------------------------------------------- */
    /*                               Aggregation                                  */
    /* -------------------------------------------------------------------------- */
    /** Enables the column for value aggregation. */ 
    enableValue?: boolean;

    /**
     * The active aggregation function for this column.
     * - `undefined` lets the column type supply its default aggFunc.
     * - `null`: no aggregation applied. */
    aggFunc?: aggFunc;

    /** Default aggregation function used if the user adds the column to Values later. */
    initialAggFunc?: aggFunc;


    /* -------------------------------------------------------------------------- */
    /*                               Security & Meta                              */
    /* -------------------------------------------------------------------------- */

    /** Security rule keys required to display this column.
     * - If undefined, always allowed.
     * - All listed rules must pass `hasPermission`.
     * - Columns failing security checks are omitted from the report.
     */
    securityRuleKeys?: SecurityRuleKeys[];

    /** Metadata used for hierarchy-type columns.
     * - Enables dynamic renaming and hiding of unused hierarchy levels. */
    hierarchyColumnOptions?: ReportHierarchyColumnOptions;

    

    /* -------------------------------------------------------------------------- */
    /*                               Runtime Fields                               */
    /* -------------------------------------------------------------------------- */

    /** Runtime-only field name used for data binding (e.g., compact Key or API alias).
     * - Not part of the registry definition. */
    field?: string;
}


/* -------------------------------------------------------------------------- */
/*                   Hierarchy-Specific Column Configuration                  */
/* -------------------------------------------------------------------------- */

/** Configuration for hierarchy-specific columns. */
export interface ReportHierarchyColumnOptions {
    /** Hierarchy this column belongs to (e.g., Division, Delivery Center). */
    hierarchyType: HierarchyType

    /** Hierarchy level sequence.
     * - 1 = Root level.
     * - 5 = Leaf level. */
    levelSequence: 1 | 2 | 3 | 4 | 5;
}



/* -------------------------------------------------------------------------- */
/*                          Column & Layout Configuration                     */
/* -------------------------------------------------------------------------- */

/**
 * Defines a column reference within a report layout.
 * 
 * Allows overriding specific properties of the base column definition.
 */
export interface ReportColumnConfig {
    /** Column key referencing the base definition in the registry. */
    key: ReportColumnKeys;

    /** Optional overrides applied to the base column definition.  */
    overrides?: Partial<ReportColumnDefinition>;
}



/* -------------------------------------------------------------------------- */
/*                            Column Group Definition                         */
/* -------------------------------------------------------------------------- */

/** Defines a shared column group header (used for column grouping in AG Grid). */
export interface ReportColumnGroupDefinition {
    /** Group identifier used by column definitions */
    key: string;

    /** Display text for the grouped column header. */
    header: string;
}


/** Associates a group key with optional override settings. */
export interface ReportColumnGroupConfig {
    /** Key referencing a registered column group. */
    key: ReportColumnGroupKeys,

    /** Optional overrides applied to the base column group definition. */
    overrides?: Partial<ReportColumnGroupDefinition>
}